<template>
    <div class="date-picker" :id="componentId" :ref="componentId">
        <input
            type="text"
            class="input"
            :value="formattedValue"
            :placeholder="placeholder"
            @focus.prevent="open"
            @input="handleInput($event.target.value)"
        />
        <div v-if="isOpen"
             class="date-picker-popup"
             :class="{'align-right': nearTheEdge}"
             v-iron-source-click-outside="{ handler: 'close', exclude: [componentId] }">
            <DatePickerMonth
                :id="componentId + '-month'"
                :selected="valueAsDate"
                :allowFutureDates="allowFutureDates"
                @update:selected="updateValue"
            />
        </div>
    </div>
</template>

<script>
import ironSourceClickOutside from 'directives/IronSourceClickOutside';
import dateServices from 'components/dateRange/DateServices';
import moment from 'moment';
import DatePickerMonth from './DatePickerMonth.vue';

export default {
    name: 'DatePicker',
    components: { DatePickerMonth },
    props: {
        uniqueId: String,
        value: {
            type: [Date, String],
            default: null
        },
        allowFutureDates: {
            type: Boolean,
            default: false
        },
        dateFormat: {
            type: String,
            default: 'YYYY-MM-DD'
        },
        placeholder: {
            type: String,
            default() {
                return this.dateFormat;
            }
        }
    },
    data() {
        return {
            isOpen: false
        };
    },
    computed: {
        componentId() {
            return `datePicker${this.uniqueId || Math.random().toString().substring(2, 7)}`;
        },
        formattedValue() {
            const date = moment(this.value, this.dateFormat, true);
            return date.isValid() ? moment(this.value).format(this.dateFormat) : this.value;
        },
        nearTheEdge() {
            const ref = this.$refs[this.componentId];
            if (!ref || !this.isOpen) return false;
            const rect = ref.getBoundingClientRect();
            const popupWidth = 232; // should be the same as width in styles
            return window.innerWidth - (rect.x + popupWidth) < 0;
        },
        valueAsDate() {
            if (!this.value) return null;
            const parsedDate = moment(this.value, this.dateFormat);
            return parsedDate.isValid() ? parsedDate.toDate() : null;
        }
    },
    methods: {
        open() {
            this.isOpen = true;
        },
        close() {
            this.isOpen = false;
        },
        updateValue(value) {
            this.close();
            this.$emit('update:value', value);
        },
        handleInput(val) {
            if (!this.isOpen) {
                this.$emit('update:value', val);
                return;
            }

            const parsedDate = moment(val, this.dateFormat, true);
            if (!val.trim()) {
                this.updateValue(null);
            }
            // ignore if invalid date
            if (!parsedDate.isValid()) return;
            // ignore if future date
            if (!this.allowFutureDates && this.isFutureDate(parsedDate)) return;
            // else trigger update
            this.updateValue(parsedDate.toDate());
        },
        isFutureDate(date) {
            const now = dateServices.getCurrentTimeUTC();
            const today = moment(now).startOf('day');
            return date.diff(today, 'days') > 0;
        }
    },
    directives: {
        ironSourceClickOutside
    }
};
</script>

<style lang="scss" scoped>
@import "~styles/styleGuide.scss";
@import "~styles/input.scss";

.date-picker {
    position: relative;
    .input {
        max-width: 100%;
    }
}

.date-picker-popup {
    width: 232px;
    padding-left: 7px;
    padding-bottom: 10px;
    position: absolute;
    top: 38px;
    left: 0;
    z-index: getZIndexLayerOffset(normal, 15);
    background-color: $white;
    border-radius: $borderRadius;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.3);
    &.align-right {
        left: inherit;
        right: 0;
    }
}

</style>
